import React, { useCallback, useMemo } from 'react';
import { string } from 'prop-types';
import { useArrayLoader } from '../../../data-table/Loaders';
import { useDataService } from '../../../../services/ApiDataService';
import { useAsyncDataFetch } from '../../../../utilities/Hooks';
import {
    CLASSIFIER_LOG_COLUMNS,
    CLASSIFIER_LOG_LIST_URL,
    CLASSIFIER_LOG_SORT_FIELD_INFO,
    getClassifierLogListRequestParams,
} from '../InfrastructureHelper';
import DataTable from '../../../data-table/DataTable';

const EMPTY_STATE_MESSAGE = 'No monitoring details available for this device';
const TABLE_ID = 'ClassifierLog';

/**
 * Table component displaying classifier logs for a specific device
 * @param {Object} props - Component props
 * @param {string} props._id - Device ID to fetch classifier logs for
 * @returns {JSX.Element} DataTable with classifier logs
 */
const ClassifierLogTable = ({ _id }) => {
    const dataService = useDataService();

    const fetchClassifierLog = useCallback(() => {
        const params = getClassifierLogListRequestParams(_id);
        return dataService.get(CLASSIFIER_LOG_LIST_URL, params);
    }, [_id, dataService]);

    const { data: classifierLogs, emptyText } = useAsyncDataFetch({
        fetchFn: fetchClassifierLog,
        emptyStateMessage: EMPTY_STATE_MESSAGE,
        dependencies: [fetchClassifierLog],
    });

    const loader = useArrayLoader(classifierLogs, (data) => data);

    const dataTableConfig = useMemo(
        () => ({
            tableId: TABLE_ID,
            columns: CLASSIFIER_LOG_COLUMNS,
            sorters: CLASSIFIER_LOG_SORT_FIELD_INFO,
            disableColumnRemover: true,
            disableCSVExport: true,
            showSearch: false,
            showToolbar: false,
        }),
        []
    );

    return <DataTable {...dataTableConfig} emptyText={emptyText} loader={loader} />;
};

ClassifierLogTable.propTypes = {
    _id: string.isRequired,
};

export default ClassifierLogTable;
